import { useEffect, useState } from 'react';
import { BuildStatus, Spinner } from './BuildStatus.tsx';
import { StatusChip, type ChipTone } from './StatusChip.tsx';
import { deriveStatus, type FleetStatus, type StatusFlags } from '../lib/fleetStatus.ts';
import { Icon } from './Icon.tsx';
import { Toast, type ToastState } from './Toast.tsx';
import { callApi } from '../lib/api.ts';
import { isRecent } from '../lib/clientTokens.ts';
import { isDeployBuild } from '../lib/deployJobs.ts';
import { formatDuration } from '../lib/duration.ts';
import { formatUsd } from '../lib/money.ts';
import { btnGhost, btnSolid, btnDanger, card, sectionTitle, sectionDesc } from '../lib/ui.ts';
import { FlapText } from './FlapText.tsx';
import type { Assignment, Build, BuildTotals, Client, FleetLatestBuild, Profile } from '../lib/types.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { maintainResume, inFlightBuildId } from '../lib/resumeFlows.ts';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Admin Fleet View (spec §16) — the portfolio cockpit. One row per client,
 * derived entirely from data we already hold (clients + builds + assignments).
 *
 * READ-ONLY by construction: it adds no write path to any client site. It is
 * admin-only by construction too — it is only ever rendered inside
 * AdminDashboard, which App.tsx shows only when role === 'admin', and every
 * query feeding it is RLS-scoped (admin reads all; designers can't reach here).
 *
 * Last-deploy status reuses the existing navigation-proof, exact-commit
 * BuildStatus mechanism (polls /api/build-status + /api/promote-status); it is
 * not rebuilt here.
 */
// One semantic status pill everywhere (rebrand PR-B). go-live-protection §4 (Decision B):
// the two old "LIVE" pills (live-DEPLOYED vs live-PROTECTED) are COLLAPSED into one — going
// live always flips is_live, so a promoted app-managed client IS live/protected. The pill is
// `live-protected` (label "LIVE"); the lock badge is driven by the client's REAL is_live at
// the call site, so a transitional promoted-but-not-yet-is_live client (e.g. David pre-
// migration) reads "LIVE" WITHOUT a false lock. `paused` keeps its non-neutral (warning) tone.
const STATUS_STYLE: Record<FleetStatus, { label: string; tone: ChipTone }> = {
  'live-protected': { label: 'LIVE', tone: 'success' },
  building: { label: 'building', tone: 'info' },
  'in-preview': { label: 'in-preview', tone: 'warning' },
  draft: { label: 'draft', tone: 'neutral' },
  paused: { label: 'paused', tone: 'warning' },
};

// Fallback flags from the (windowed) builds list — used only when the full-table
// latest/flags aren't available (e.g. pre-mig-0019).
function flagsFromBuilds(builds: Build[]): StatusFlags {
  return {
    hasInflight: builds.some((b) => b.outcome === 'queued' || b.outcome === 'running'),
    hasPromoted: builds.some((b) => !!b.promote_commit_sha),
    hasPreview: builds.some((b) => !!b.preview_url || b.outcome === 'success'),
  };
}

// Canonical production domain: an explicit (custom-domain-capable) production_url
// wins, else the conventional Cloudflare Pages domain derived from pages_project.
function productionUrl(client: Client): string | null {
  if (client.production_url) return client.production_url;
  if (client.pages_project) return `https://${client.pages_project}.pages.dev`;
  return null;
}

// AI jobs run the bounded agent loop (job_type 'agent_job:<name>'); everything
// else is the deterministic token→theme assembly.
function isDeterministic(b: Build): boolean {
  return !b.job_type.startsWith('agent_job');
}

// #GR32/#GR35: isDeployBuild — which builds are real SITE deploys — now lives in the
// shared src/lib/deployJobs.ts (FleetView + AdminDashboard + mig 0026 all agree).

export function FleetView({
  clients,
  builds,
  assignments,
  designers,
  totals,
  latest,
  onChange,
}: {
  clients: Client[];
  builds: Build[];
  assignments: Assignment[];
  designers: Profile[];
  /** #GR18 durable per-client metering (cost/tokens/time/builds) keyed by client_id —
   *  cumulative over the WHOLE builds table, so it never "resets". Falls back to the
   *  (windowed) `builds` prop when absent (e.g. the aggregate endpoint failed). */
  totals?: Record<string, BuildTotals>;
  /** #GR19: per-client LATEST build + status flags over the WHOLE builds table (keyed by
   *  client_id), so Status/Last-deploy/preview/prod aren't windowed by the capped list.
   *  Falls back to the (windowed) `builds` prop when absent (e.g. pre-mig-0019). */
  latest?: Record<string, FleetLatestBuild>;
  /** Refresh the fleet after a client is retired (its row should disappear). */
  onChange?: () => void;
}) {
  const designerById = new Map(designers.map((d) => [d.id, d]));
  const [toast, setToast] = useState<ToastState | null>(null);

  return (
    <section className={`${card} space-y-3 p-5`}>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div>
        <h2 className={sectionTitle}>Fleet ({clients.length})</h2>
        <p className={sectionDesc}>
          Admin portfolio cockpit — monitoring only. Live status, last deploy, owners and
          spend per client. 🔒 clients are protected: the app refuses all writes to them.
        </p>
      </div>

      {/* Collapsible — keep the long portfolio tidy; open by default. */}
      <details open className="group">
        <summary className="cursor-pointer list-none text-xs font-medium text-ink-2 hover:text-ink">
          <span className="group-open:hidden">▸ Show fleet ({clients.length})</span>
          <span className="hidden group-open:inline">▾ Hide fleet</span>
        </summary>
        <div className="mt-2 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line text-xs uppercase tracking-wide text-ink-2">
            <tr>
              <Th>Client</Th>
              <Th>Production</Th>
              <Th>Status</Th>
              <Th>Last deploy</Th>
              <Th>Designers</Th>
              <Th>Builds</Th>
              <Th>Cost</Th>
              <Th>Tokens</Th>
              <Th>Build time</Th>
              <Th>Auto / AI (recent)</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const cb = builds
                .filter((b) => b.client_id === c.id)
                .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
              // #GR32: the Last-deploy + status fallback derives from SITE-DEPLOY builds
              // only — a never-built client's provision/SEO/brand rows are not deploys, so
              // they must not drive "building"/"in-preview" or the CF-polling chip. (cb
              // still holds ALL builds for the cost/tokens/builds metering below.) The view
              // (0026) already filters the same way; this covers the pre-view fallback.
              const deployBuilds = cb.filter(isDeployBuild);
              // #GR19: Status + Last-deploy from the full-table view (not the capped list),
              // so a client whose builds are all older than the newest-100 still reads
              // correctly. Fall back to the windowed list when the view isn't there yet.
              const lb = latest?.[c.id];
              const status = deriveStatus(c, lb ? { hasInflight: lb.hasInflight, hasPromoted: lb.hasPromoted, hasPreview: lb.hasPreview } : flagsFromBuilds(deployBuilds));
              const latestBuild: FleetLatestBuild | Build | undefined = lb ?? deployBuilds[0];
              const prodUrl = productionUrl(c);
              const owners = assignments
                .filter((a) => a.client_id === c.id)
                .map((a) => designerById.get(a.designer_id))
                .filter((d): d is Profile => !!d);
              // #GR18: cost/tokens/time/builds from the durable aggregate (cumulative,
              // never reset). Fall back to the windowed `builds` prop if it's absent.
              const t = totals?.[c.id];
              const cost = t ? t.costUsd : cb.reduce((s, b) => s + Number(b.cost_usd), 0);
              const tokens = t ? t.tokens : cb.reduce((s, b) => s + b.input_tokens + b.output_tokens, 0);
              const buildCount = t ? t.builds : cb.length;
              const durationMs = t ? t.durationMs : cb.reduce((s, b) => s + Number(b.duration_ms), 0);
              const det = cb.filter(isDeterministic).length;
              const ai = cb.length - det;
              const st = STATUS_STYLE[status];

              return (
                <tr key={c.id} className="border-b align-top last:border-0">
                  <Td>
                    <div className="flex items-center gap-1.5 font-medium">
                      {c.is_live && <Icon name="lock" title="Live / protected — app writes blocked" className="inline-block h-3.5 w-3.5 align-[-0.15em] text-warning" />}
                      {c.name}
                    </div>
                    <div className="text-xs text-ink-2">/{c.slug}</div>
                  </Td>
                  <Td>
                    {prodUrl ? (
                      <a
                        href={prodUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-600 underline"
                      >
                        {prodUrl.replace(/^https?:\/\//, '')}
                      </a>
                    ) : (
                      <span className="text-xs text-ink-2">—</span>
                    )}
                  </Td>
                  <Td>
                    <StatusChip tone={st.tone}>
                      {/* go-live-protection §4: the lock reflects the client's REAL protection
                          (is_live), not merely "promoted" — so a transitional promoted-but-not-
                          yet-is_live client reads "LIVE" without a false lock. */}
                      {c.is_live && <Icon name="lock" title="protected — app writes blocked" />}
                      {st.label}
                    </StatusChip>
                  </Td>
                  <Td>
                    {latestBuild ? (
                      <div className="space-y-1">
                        <div className="text-xs text-ink-2">
                          {new Date(latestBuild.created_at).toLocaleString()}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs text-ink-2">preview</span>
                          <BuildStatus
                            buildId={latestBuild.id}
                            initialUrl={latestBuild.preview_url}
                            live={
                              latestBuild.outcome === 'success' &&
                              !latestBuild.preview_url &&
                              isRecent(latestBuild.created_at)
                            }
                          />
                        </div>
                        {latestBuild.promote_commit_sha && (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-ink-2">prod</span>
                            <BuildStatus
                              buildId={latestBuild.id}
                              initialUrl={latestBuild.production_url}
                              live={
                                !latestBuild.production_url &&
                                isRecent(latestBuild.promoted_at ?? latestBuild.created_at)
                              }
                              endpoint="promote-status"
                              readyLabel={<>Live <Icon name="check" title="live" /></>}
                              openLabel={<>open live <Icon name="external" /></>}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-ink-2">No deploys yet</span>
                    )}
                  </Td>
                  <Td>
                    {owners.length > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        {owners.map((d) => (
                          <span key={d.id} className="text-xs text-ink-2">
                            {d.email}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-warning">unassigned</span>
                    )}
                  </Td>
                  {/* Feature/metric numbers → font-serif (Bricolage Grotesque), tabular+lining so columns align. */}
                  <Td className="font-serif nums-tabular">{buildCount}</Td>
                  <Td className="font-serif nums-tabular">{formatUsd(cost)}</Td>
                  <Td className="font-serif nums-tabular">{tokens.toLocaleString()}</Td>
                  <Td className="font-serif nums-tabular text-xs">{formatDuration(durationMs)}</Td>
                  <Td>
                    <span className="text-xs text-ink-2" title="Automatic builds vs AI builds — from recent activity">
                      {det} / {ai}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex flex-col items-start gap-1.5">
                      <MaintainClient client={c} builds={cb} onChange={onChange} onToast={setToast} />
                      <RetireClient client={c} builds={cb} onDone={onChange} onToast={setToast} />
                    </div>
                  </Td>
                </tr>
              );
            })}
            {clients.length === 0 && (
              <tr>
                <td colSpan={11} className="px-3 py-4 text-sm text-ink-2">
                  No clients yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </details>
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium text-ink-2">{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2${className ? ` ${className}` : ''}`}>{children}</td>;
}

/**
 * Per-client "Retire" action (retire-client.md) — tears the client down across all
 * three layers (Cloudflare · monorepo · Supabase) via the async retire endpoint,
 * then polls to completion. A live client requires typing its name to confirm
 * (the deliberate live-protection override); a throwaway needs only the modal's
 * single confirm. On success the row vanishes (onDone refreshes the fleet).
 */
function RetireClient({
  client,
  builds,
  onDone,
  onToast,
}: {
  client: Client;
  builds: Build[];
  onDone?: () => void;
  onToast?: (t: ToastState) => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const confirmed = !client.is_live || typed.trim() === client.name || typed.trim() === client.slug;

  // #GR90: poll an already-started teardown to terminal (extracted so a resumed run shares it).
  async function pollTeardown(buildId: string) {
    setPhase('Tearing down (Cloudflare → monorepo → database)…');
    const started = Date.now();
    while (Date.now() - started < 15 * 60 * 1000) {
      await sleep(5_000);
      let st: { status: string; error?: string | null };
      try {
        st = await callApi('retire-client-status', {
          method: 'POST',
          body: JSON.stringify({ buildId, clientId: client.id }),
        });
      } catch {
        continue; // transient — keep polling
      }
      if (st.status === 'done') {
        setOpen(false);
        setBusy(false);
        onToast?.({ message: `Retired ${client.name}.` });
        onDone?.();
        return;
      }
      if (st.status === 'failed') {
        setErr(friendlyError(st.error, st.error ?? 'Retire failed — the client was left intact. You can retry.'));
        setBusy(false);
        return;
      }
    }
    setErr('Timed out waiting for the teardown.');
    setBusy(false);
  }

  async function retire() {
    setBusy(true);
    setErr(null);
    setPhase('Starting teardown…');
    try {
      const res = await callApi<{ buildId: string }>('retire-client', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, confirmName: client.is_live ? typed.trim() : undefined }),
      });
      await pollTeardown(res.buildId);
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
      setBusy(false);
    }
  }

  // #GR90: resume a teardown already in flight after a remount (reload mid-retire) so its progress
  // doesn't vanish — reopen the modal into the busy/polling state against the derived retire build.
  // (The row vanishes on done regardless; this only keeps the in-flight progress visible.)
  const resumeRetireId = inFlightBuildId(builds, 'retire');
  useEffect(() => {
    if (resumeRetireId && !busy) {
      setOpen(true);
      setBusy(true);
      void pollTeardown(resumeRetireId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeRetireId]);

  return (
    <>
      <button
        onClick={() => {
          setOpen(true);
          setTyped('');
          setErr(null);
        }}
        className="rounded border border-danger px-2 py-1 text-xs text-danger transition-colors hover:bg-danger hover:text-paper"
      >
        Retire
      </button>

      {open && (
        // Centered overlay (replaces the cramped in-cell w-64 panel).
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="w-full max-w-md space-y-3 rounded-lg border border-danger bg-paper p-5 text-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-base font-bold text-danger">Retire {client.name}?</p>
            <p className="text-ink-2">
              Permanently removes it across all three layers — Cloudflare Pages project · the monorepo
              (<span className="font-mono">sites/{client.slug}</span> on main + <span className="font-mono">app/{client.slug}</span>) ·
              the app database (client + its builds/assignments/chat). This can’t be undone.
            </p>
            {client.is_live && (
              <div className="space-y-1 rounded border border-danger bg-paper-2 p-2">
                <p className="flex items-center gap-1 font-medium text-danger"><Icon name="lock" /> This client is live/protected. Type its name to confirm:</p>
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={client.name}
                  disabled={busy}
                  className="w-full rounded border border-danger px-2 py-1 outline-none focus:ring-1 focus:ring-danger"
                />
              </div>
            )}
            {busy && (
              <p className="rounded border border-warning-bright bg-paper-2 p-2 text-warning">
                {phase} (this takes ~1–2 min — the monorepo regens its lockfile)
              </p>
            )}
            {err && <p className="rounded border border-danger bg-paper-2 p-2 text-danger">{err}</p>}
            <div className="flex items-center justify-end gap-3 pt-1">
              <button onClick={() => setOpen(false)} disabled={busy} className={btnGhost}>
                Cancel
              </button>
              <button onClick={() => void retire()} disabled={busy || !confirmed} className={btnDanger}>
                {busy ? phase || 'Retiring…' : 'Retire'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

type PinUpdate = { name: string; from: string; to: string };
type CheckResult = { slug: string; protected: boolean; updates: PinUpdate[]; upToDate: boolean };

/**
 * Per-client "Update libraries" action (maintain-flow.md §6) — the Maintain mirror of Retire.
 * On mount it runs maintain-check (app/<slug> `@alchmystudio/*` pins vs SEED_PINS); a client whose
 * pins are behind gets an "Update available" trigger. The modal drives the flow: Apply (bump +
 * regen on app/<slug>, polled to a green preview via maintain-status) → Go live (promote via the §5
 * live carve-out, polled via promote-status). Protected/up-to-date clients render nothing. Nothing
 * touches the live site until the explicit Go live; Cooper is protected-slug-excluded either way.
 */
function MaintainClient({
  client,
  builds,
  onChange,
  onToast,
}: {
  client: Client;
  builds: Build[];
  onChange?: () => void;
  onToast?: (t: ToastState) => void;
}) {
  const [check, setCheck] = useState<CheckResult | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [applyBuildId, setApplyBuildId] = useState<string | null>(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [goliveBuildId, setGoliveBuildId] = useState<string | null>(null);
  const [live, setLive] = useState(false);

  // #GR90: derive the in-flight/green-unpublished maintain flow from persisted build rows so the
  // modal RESUMES on reopen (and a reload mid-flow) instead of resetting — making the
  // "Safe to leave this page — it keeps running" copy true. optimistic click state takes precedence.
  const resume = maintainResume(builds);
  const effApplyBuildId = applyBuildId ?? resume.applyBuildId;
  const effGoliveBuildId = goliveBuildId ?? resume.goliveBuildId;

  useEffect(() => {
    let alive = true;
    void callApi<CheckResult>('maintain-check', { method: 'POST', body: JSON.stringify({ clientId: client.id }) })
      .then((r) => alive && setCheck(r))
      .catch(() => {
        /* a check failure just leaves the row without an update affordance — non-blocking */
      });
    return () => {
      alive = false;
    };
  }, [client.id]);

  const hasUpdate = !!check && !check.protected && !check.upToDate && check.updates.length > 0;
  // #GR90: once apply() has run, maintain-check reports "up to date" (the branch pins now match
  // SEED_PINS) even though the update isn't PUBLISHED — so hasUpdate goes false. Without a resume
  // affordance the operator would be stranded: green preview, no way to reopen and finish Go-live.
  // resume.active (an in-flight/green-unpublished/publishing maintain, derived from build rows)
  // keeps the row actionable regardless of hasUpdate.
  const canResume = resume.active;
  // Phase 5 item 3: reserve the Actions slot instead of returning null when there's no
  // update — a muted, non-interactive affordance keeps the column from jumping.
  if (!hasUpdate && !canResume && !open) {
    return (
      <span className="text-xs text-ink-2" title="Libraries are current — no update available.">
        {check == null ? '—' : check.protected ? 'Protected' : 'Up to date'}
      </span>
    );
  }

  async function apply() {
    setBusy(true);
    setErr(null);
    try {
      const r = await callApi<{ buildId: string }>('maintain-apply', { method: 'POST', body: JSON.stringify({ clientId: client.id }) });
      setApplyBuildId(r.buildId);
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function golive() {
    setBusy(true);
    setErr(null);
    try {
      const r = await callApi<{ buildId: string }>('maintain-golive', { method: 'POST', body: JSON.stringify({ clientId: client.id }) });
      setGoliveBuildId(r.buildId);
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setOpen(false);
    // reset the flow so reopening starts clean, and refresh the fleet + this row's check.
    setApplyBuildId(null);
    setPreviewReady(false);
    setGoliveBuildId(null);
    setLive(false);
    setErr(null);
    void callApi<CheckResult>('maintain-check', { method: 'POST', body: JSON.stringify({ clientId: client.id }) })
      .then(setCheck)
      .catch(() => {});
    onChange?.();
  }

  return (
    <>
      {hasUpdate && (
        <button
          onClick={() => setOpen(true)}
          className="rounded border border-warning-bright px-2 py-1 text-xs text-warning transition-colors hover:bg-warning-bright hover:text-paper"
          title={`Library update available: ${check!.updates.map((u) => `${u.name.replace('@alchmystudio/', '')} ${u.from}→${u.to}`).join(', ')}`}
        >
          <Icon name="arrow-up" /> Update libraries
        </button>
      )}
      {/* #GR90: an update is applied but not yet published → a resume affordance so the operator
          can reopen and finish Go-live (hasUpdate is false here — the branch pins already match). */}
      {!hasUpdate && canResume && !open && (
        <button
          onClick={() => setOpen(true)}
          className="rounded border border-warning-bright px-2 py-1 text-xs text-warning transition-colors hover:bg-warning-bright hover:text-paper"
          title="A library update is in progress — resume to finish going live."
        >
          <Icon name="arrow-up" /> {effGoliveBuildId ? 'Publishing…' : 'Resume update'}
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && close()}>
          <div className="w-full max-w-md space-y-3 rounded-lg border border-line bg-paper p-5 text-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-base font-bold text-ink">Update {client.name}’s libraries</p>
            <p className="text-ink-2">
              Bumps the client’s <span className="font-mono">@alchmystudio/*</span> pins to the current published
              versions and rebuilds the preview. Nothing touches the live site until you click <b>Go live</b>.
            </p>

            <ul className="rounded border border-line bg-paper-2 p-2 font-mono text-xs">
              {(check?.updates ?? []).map((u) => (
                <li key={u.name}>
                  {u.name} <span className="text-ink-2">{u.from}</span> → <span className="text-ink">{u.to}</span>
                </li>
              ))}
            </ul>

            {/* Step 1 — Apply (bump + regen → green preview). */}
            {!effApplyBuildId ? (
              <button onClick={() => void apply()} disabled={busy} className={btnSolid}>
                {busy ? (
                  <>
                    <Spinner /> <FlapText>Applying…</FlapText>
                  </>
                ) : (
                  <FlapText>Apply update</FlapText>
                )}
              </button>
            ) : (
              <div className="space-y-1 rounded border border-line bg-paper-2 p-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-ink-2">preview</span>
                  <BuildStatus buildId={effApplyBuildId} initialUrl={null} live endpoint="maintain-status" onResolved={() => setPreviewReady(true)} />
                </div>
                <p className="text-[11px] text-ink-2">Bumping pins + regenerating the lockfile, then Cloudflare rebuilds (~a few min).</p>
                <p className="text-[11px] text-ink-2">Safe to leave this page — it keeps running.</p>
              </div>
            )}

            {/* Step 2 — Go live (promote; enabled once the preview is green). */}
            {effApplyBuildId && (
              <div className="space-y-1">
                <button onClick={() => void golive()} disabled={busy || !previewReady || !!effGoliveBuildId} className={btnSolid} title={previewReady ? '' : 'Waiting for the preview to go green'}>
                  {effGoliveBuildId ? (
                    <>
                      <Spinner /> <FlapText>Going live…</FlapText>
                    </>
                  ) : previewReady ? (
                    <FlapText>Go live</FlapText>
                  ) : (
                    <FlapText>Go live (preview building…)</FlapText>
                  )}
                </button>
                {effGoliveBuildId && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-ink-2">prod</span>
                    <BuildStatus
                      buildId={effGoliveBuildId}
                      initialUrl={null}
                      live
                      endpoint="promote-status"
                      readyLabel={<>Live <Icon name="check" title="live" /></>}
                      openLabel={<>open live <Icon name="external" /></>}
                      onResolved={() => {
                        if (live) return;
                        setLive(true);
                        onToast?.({ message: `${client.name} libraries updated + live.` });
                      }}
                    />
                  </div>
                )}
                {effGoliveBuildId && <p className="text-[11px] text-ink-2">Safe to leave this page — it keeps running.</p>}
              </div>
            )}

            {err && <p className="rounded border border-danger bg-paper-2 p-2 text-danger">{err}</p>}
            <div className="flex items-center justify-end gap-3 pt-1">
              <button onClick={close} disabled={busy} className={btnGhost}>
                {live || (effApplyBuildId && !effGoliveBuildId) ? 'Done' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
